import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURL from '../../controllers/Api'; // Adjust path to match your project structure
import { toast } from '../../utils/toast';
import { formatKolkataDate, formatKolkataDateTime } from '../../utils/dateTime';

const Profile = () => {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userType, setUserType] = useState('individual');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [removingResume, setRemovingResume] = useState(false);
    const [selectedResume, setSelectedResume] = useState(null);
    const [activeTab, setActiveTab] = useState('personal');
    const [extractionLoading, setExtractionLoading] = useState(false);
    const [resumeDetails, setResumeDetails] = useState({});
    const [uploadReport, setUploadReport] = useState(null);
    const [selectedCompanyResumeIds, setSelectedCompanyResumeIds] = useState([]);
    const [companyResumeBusy, setCompanyResumeBusy] = useState(false);

    // Upload report filters
    const [uploadLogStatus, setUploadLogStatus] = useState('all');
    const [uploadLogDateFrom, setUploadLogDateFrom] = useState('');
    const [uploadLogDateTo, setUploadLogDateTo] = useState('');

    // Static profile image fallback

    const STATIC_PROFILE_IMAGE = 'https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff&size=128';
    const STATIC_COMPANY_IMAGE = 'https://ui-avatars.com/api/?name=Company&background=3B82F6&color=fff&size=128';
    const STATIC_COVER_IMAGE = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1200&h=200&fit=crop';

    const [profile, setProfile] = useState({
        user: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            location: '',
            profileImage: '',
            coverImage: '',
            userType: 'individual',
            companyName: ''
        },
        profile: {
            headline: '',
            bio: '',
            website: '',
            linkedin: '',
            github: '',
            industry: '',
            experienceYears: '',
            currentPosition: '',
            currentCompany: '',
            educationSummary: '',
            profileStrength: 0
        },
        company: {
            companyName: '',
            website: '',
            size: '',
            industry: '',
            phone: '',
            location: '',
            description: '',
            establishedYear: '',
            companyType: '',
            benefits: '',
            socialLinks: '',
            logoUrl: ''
        },
        skills: [],
        experiences: [],
        education: [],
        certifications: [],
        resumes: []
    });

    useEffect(() => {
        checkUserType();
        fetchProfile();
    }, []);

    useEffect(() => {
        if (userType === 'company') {
            fetchUploadReport();
        }
    }, [userType]);

    const fetchUploadReport = async () => {
        try {
            const token = localStorage.getItem('token');
            const userStr = localStorage.getItem('user');
            let userId = null;
            if (userStr) {
                try {
                    const parsed = JSON.parse(userStr);
                    userId = parsed?.id;
                } catch (e) {}
            }

            if (!userId) {
                throw new Error('Missing user id in localStorage');
            }

            const response = await fetch(`${ApiURL}/get_staging_resumes.php?user_id=${userId}`, {
                headers: {
                    'Authorization': `Bearer ${token || userId}`
                }
            });
            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setUploadReport(result.data);
                }
            }
        } catch (error) {
            console.error('Error fetching upload report:', error);
        }
    };

    const fetchCompanyResumes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/get_visible_resumes.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.success === false) {
                throw new Error(result.message || 'Failed to load company resumes');
            }
            const rows = Array.isArray(result.data) ? result.data : [];
            setProfile(prev => ({ ...prev, resumes: rows }));
            const detailsMap = {};
            rows.forEach((resume) => { if (resume?.id) detailsMap[resume.id] = resume; });
            setResumeDetails(detailsMap);
            setSelectedCompanyResumeIds(prev => prev.filter(id => rows.some(r => Number(r.id) === Number(id))));
        } catch (error) {
            console.error('Error fetching company resumes:', error);
            toast.error(error.message || 'Failed to load company resumes');
        }
    };

    const runCompanyResumeAction = async (action, resumeIds) => {
        const ids = Array.from(new Set((resumeIds || []).map(Number).filter(Boolean)));
        if (!ids.length) {
            toast.warning('Select at least one resume');
            return;
        }
        if (ids.length > 10) {
            toast.warning('You can process maximum 10 resumes at one time');
            return;
        }
        if (action === 'delete' && !window.confirm(`Delete ${ids.length} selected resume${ids.length > 1 ? 's' : ''}? This cannot be undone.`)) {
            return;
        }

        setCompanyResumeBusy(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}crm/company/bulk_resume_actions.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ action, resume_ids: ids })
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.success === false) {
                throw new Error(result.message || `Failed to ${action} resumes`);
            }
            const failed = Number(result?.data?.failed || 0);
            const succeeded = Number(result?.data?.succeeded ?? result?.data?.successful ?? 0);
            if (failed > 0) toast.warning(`${succeeded} completed, ${failed} failed`);
            else toast.success(action === 'delete' ? `${succeeded || ids.length} resume(s) deleted` : `${succeeded || ids.length} resume(s) re-extracted`);
            setSelectedCompanyResumeIds([]);
            await fetchUploadReport();
        } catch (error) {
            console.error('Company resume action failed:', error);
            toast.error(error.message || 'Resume action failed');
        } finally {
            setCompanyResumeBusy(false);
        }
    };

    const toggleCompanyResumeSelection = (resumeId) => {
        const id = Number(resumeId);
        setSelectedCompanyResumeIds(prev => {
            if (prev.includes(id)) return prev.filter(item => item !== id);
            if (prev.length >= 10) {
                toast.warning('Maximum 10 resumes can be selected');
                return prev;
            }
            return [...prev, id];
        });
    };

    const checkUserType = () => {
        const type = localStorage.getItem('userType') || 'individual';
        setUserType(type);
        console.log('User type from localStorage:', type);
    };
    

    // Update the fetchProfile function to properly set experiences and education
    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            // Get user ID from localStorage
            const userStr = localStorage.getItem('user');
            let userId = null;

            if (userStr) {
                try {
                    const user = JSON.parse(userStr);
                    userId = user.id;
                    console.log('User ID from localStorage:', userId);
                } catch (e) {
                    console.error('Error parsing user:', e);
                }
            }

            if (!userId) {
                console.error('No user ID found in localStorage');
                return;
            }

            // Pass user_id as query parameter
            const response = await fetch(`${ApiURL}/get_profile.php?user_id=${userId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    console.log('Profile data received:', result.data);

                    // Make sure experiences and education are properly set
                    setProfile({
                        user: result.data.user || {
                            firstName: '', lastName: '', email: '', phone: '', location: '', profileImage: '', coverImage: '', userType: 'individual', companyName: ''
                        },
                        profile: result.data.profile || {
                            headline: '', bio: '', website: '', linkedin: '', github: '', industry: '', experienceYears: '', currentPosition: '', currentCompany: '', educationSummary: '', profileStrength: 0
                        },
                        company: result.data.company || {
                            companyName: '', website: '', size: '', industry: '', phone: '', location: '', description: '', establishedYear: '', companyType: '', benefits: '', socialLinks: '', logoUrl: ''
                        },
                        skills: result.data.skills || [],
                        experiences: result.data.experiences || [],
                        education: result.data.education || [],
                        certifications: result.data.certifications || [],
                        resumes: result.data.resumes || []
                    });

                    // Set user type from localStorage
                    const storedType = localStorage.getItem('userType') || 'individual';
                    setUserType(storedType);

                    // Store resume details
                    if (result.data.resumes && result.data.resumes.length > 0) {
                        const detailsMap = {};
                        result.data.resumes.forEach(resume => {
                            if (resume.id) {
                                detailsMap[resume.id] = resume;
                            }
                        });
                        setResumeDetails(detailsMap);
                    }
                }
            } else {
                console.error('Profile fetch failed:', response.status);
                if (response.status === 401) {
                    toast.error('Your session has expired. Please log in again.');
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('userType');
                    navigate('/login');
                }
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchResumeDetails = async (resumeId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/get_resume_details.php?resume_id=${resumeId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setResumeDetails(prev => ({
                        ...prev,
                        [resumeId]: result.data
                    }));
                }
            }
        } catch (error) {
            console.error('Error fetching resume details:', error);
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a valid image file (JPEG, PNG, GIF, SVG)');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('Logo size must be less than 2MB');
            return;
        }

        setUploadingLogo(true);

        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('logo', file);

            const response = await fetch(`${ApiURL}/upload_company_logo.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setProfile(prev => ({
                    ...prev,
                    company: {
                        ...(prev.company || {}),
                        logoUrl: result.data.logo_url
                    }
                }));
                toast.success('Logo uploaded successfully!');
            }

        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
            event.target.value = '';
        }
    };

    const handleRemoveResume = async (resumeId) => {
        if (!window.confirm('Are you sure you want to remove this resume?')) {
            return;
        }

        setRemovingResume(true);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/remove_resume.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ resume_id: resumeId })
            });

            const result = await response.json();

            if (result.success) {
                setProfile(prev => ({
                    ...prev,
                    resumes: prev.resumes.filter(r => r.id !== resumeId)
                }));

                setResumeDetails(prev => {
                    const newDetails = { ...prev };
                    delete newDetails[resumeId];
                    return newDetails;
                });

                toast.success('Resume removed successfully!');
            }
        } catch (error) {
            console.error('Error removing resume:', error);
            toast.error('Failed to remove resume');
        } finally {
            setRemovingResume(false);
        }
    };

    const handleViewResume = async (resume) => {
        if (!resume) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/get_resume_file.php?resume_id=${resume.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 100);
            } else {
                handleDownloadResume(resume);
            }
        } catch (error) {
            console.error('Error viewing resume:', error);
            handleDownloadResume(resume);
        }
    };

    const handleDownloadResume = async (resume) => {
        if (!resume) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/get_resume_file.php?resume_id=${resume.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = resume.original_filename || 'resume.pdf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error downloading resume:', error);
            toast.error('Failed to download resume');
        }
    };

    const handleExtractFromResume = async (resumeId) => {
        setExtractionLoading(true);
        setSelectedResume(resumeId);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${ApiURL}/extract_resume_data.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ resume_id: resumeId })
            });

            const result = await response.json();

            if (result.success && result.data) {
                setResumeDetails(prev => ({
                    ...prev,
                    [resumeId]: {
                        ...prev[resumeId],
                        ...result.data,
                        last_extracted: new Date().toISOString()
                    }
                }));
                toast.success('Data extracted successfully!');
            }
        } catch (error) {
            console.error('Error extracting data:', error);
            toast.error('Failed to extract data from resume');
        } finally {
            setExtractionLoading(false);
            setSelectedResume(null);
        }
    };

    const handleSave = async (silent = false) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            let payload = {};

            if (userType === 'individual') {
                payload = {
                    user: profile.user,
                    profile: profile.profile,
                    skills: profile.skills,
                    experiences: profile.experiences,
                    education: profile.education,
                    certifications: profile.certifications
                };
            } else {
                payload = {
                    user: profile.user,
                    company: profile.company || {}
                };
            }

            const response = await fetch(`${ApiURL}/update_profile.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401) {
                toast.error('Your session has expired. Please log in again.');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('userType');
                navigate('/login');
                return;
            }

            const result = await response.json();

            if (result.success) {
                setIsEditing(false);
                if (!silent) {
                    toast.success('Profile updated successfully!');
                }
                fetchProfile();
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            if (!silent) {
                toast.error('Failed to update profile');
            }
        }
    };

    const handleAddSkill = () => {
        const newSkill = prompt('Enter new skill:');
        if (newSkill && !profile.skills.includes(newSkill)) {
            setProfile(prev => ({
                ...prev,
                skills: [...prev.skills, newSkill]
            }));
        }
    };

    const handleRemoveSkill = (index) => {
        setProfile(prev => ({
            ...prev,
            skills: prev.skills.filter((_, i) => i !== index)
        }));
    };

    const handleChange = (section, field, value) => {
        setProfile(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return formatKolkataDate(dateString);
    };

    const getFileIcon = (fileName) => {
        const ext = fileName?.split('.').pop().toLowerCase();
        if (ext === 'pdf') return '📕';
        if (ext === 'doc' || ext === 'docx') return '📘';
        return '📄';
    };

    const renderResumeDetailsTable = (resumeId) => {
        const details = resumeDetails[resumeId];
        const resume = profile.resumes?.find(r => r.id === resumeId);

        if (!details && !resume) return null;

        // Helper function to safely get education array
        const getEducation = () => {
            if (details?.education && Array.isArray(details.education)) {
                return details.education;
            }
            if (resume?.education && Array.isArray(resume.education)) {
                return resume.education;
            }
            if (resume?.education_json) {
                try {
                    const parsed = typeof resume.education_json === 'string'
                        ? JSON.parse(resume.education_json)
                        : resume.education_json;
                    return Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                    console.error('Error parsing education_json:', e);
                    return [];
                }
            }
            return [];
        };

        // Helper function to safely get experiences array
        const getExperiences = () => {
            if (details?.experiences && Array.isArray(details.experiences)) {
                return details.experiences;
            }
            if (resume?.experiences && Array.isArray(resume.experiences)) {
                return resume.experiences;
            }
            if (resume?.experience_json) {
                try {
                    const parsed = typeof resume.experience_json === 'string'
                        ? JSON.parse(resume.experience_json)
                        : resume.experience_json;
                    return Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                    console.error('Error parsing experience_json:', e);
                    return [];
                }
            }
            return [];
        };

        const educationList = getEducation();
        const experiencesList = getExperiences();

        return (
            <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-gray-900">Extracted Data</h5>
                    <button
                        onClick={() => handleExtractFromResume(resumeId)}
                        disabled={extractionLoading && selectedResume === resumeId}
                        className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                        {extractionLoading && selectedResume === resumeId ? 'Extracting...' : 'Refresh Data'}
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Field</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Value</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {/* Personal Info */}
                            <tr className="bg-blue-50">
                                <td colSpan="2" className="px-3 py-2 font-semibold text-blue-800">Personal Information</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">Name</td>
                                <td className="px-3 py-2 text-gray-900">{details?.extracted_name || resume?.extracted_name || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">Email</td>
                                <td className="px-3 py-2 text-gray-900">{details?.email || resume?.extracted_email || resume?.email || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">Phone</td>
                                <td className="px-3 py-2 text-gray-900">{details?.phone || resume?.extracted_phone || resume?.phone || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">Location</td>
                                <td className="px-3 py-2 text-gray-900">{details?.location || 'N/A'}</td>
                            </tr>

                            {/* Professional Info */}
                            <tr className="bg-green-50">
                                <td colSpan="2" className="px-3 py-2 font-semibold text-green-800">Professional Information</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">Job Title</td>
                                <td className="px-3 py-2 text-gray-900">{details?.job_title || resume?.extracted_job_title || resume?.job_title || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">Category</td>
                                <td className="px-3 py-2 text-gray-900">{details?.category || resume?.extracted_category || resume?.category || 'N/A'}</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">Experience Years</td>
                                <td className="px-3 py-2 text-gray-900">{details?.experience_years || resume?.experience_years || 0} years</td>
                            </tr>
                            <tr>
                                <td className="px-3 py-2 text-gray-600">ATS Score</td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${(details?.ats_score || resume?.ats_score || 0) >= 80 ? 'bg-green-500' :
                                                    (details?.ats_score || resume?.ats_score || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                    }`}
                                                style={{ width: `${details?.ats_score || resume?.ats_score || 0}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-gray-900">{details?.ats_score || resume?.ats_score || 0}%</span>
                                    </div>
                                </td>
                            </tr>

                            {/* Skills */}
                            {details?.skills && details.skills.length > 0 && (
                                <>
                                    <tr className="bg-purple-50">
                                        <td colSpan="2" className="px-3 py-2 font-semibold text-purple-800">Skills ({details.skills.length})</td>
                                    </tr>
                                    <tr>
                                        <td colSpan="2" className="px-3 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                {details.skills.map((skill, idx) => (
                                                    <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                                                        {skill}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                </>
                            )}

                            {/* Education */}
                            {educationList.length > 0 && (
                                <>
                                    <tr className="bg-indigo-50">
                                        <td colSpan="2" className="px-3 py-2 font-semibold text-indigo-800">Education ({educationList.length})</td>
                                    </tr>
                                    {educationList.map((edu, idx) => (
                                        <tr key={idx}>
                                            <td className="px-3 py-2 text-gray-600">
                                                {typeof edu === 'string' ? 'Education' : (edu.degree || 'Degree')}
                                            </td>
                                            <td className="px-3 py-2 text-gray-900">
                                                {typeof edu === 'string'
                                                    ? edu
                                                    : `${edu.institution || ''} ${edu.year ? `(${edu.year})` : ''}`}
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            )}

                            {/* Experience */}
                            {experiencesList.length > 0 && (
                                <>
                                    <tr className="bg-orange-50">
                                        <td colSpan="2" className="px-3 py-2 font-semibold text-orange-800">Work Experience ({experiencesList.length})</td>
                                    </tr>
                                    {experiencesList.map((exp, idx) => (
                                        <tr key={idx}>
                                            <td className="px-3 py-2 text-gray-600">
                                                {typeof exp === 'string' ? 'Position' : (exp.title || 'Position')}
                                            </td>
                                            <td className="px-3 py-2 text-gray-900">
                                                {typeof exp === 'string'
                                                    ? exp
                                                    : `${exp.company || ''} ${exp.start_date ? `(${exp.start_date} - ${exp.end_date || 'Present'})` : ''}`}
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-20">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8 pt-20">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <h1 className="text-3xl font-bold text-gray-900">
                            {userType === 'individual' ? 'My Profile' : 'Company Profile'}
                        </h1>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                            >
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                </div>

                {/* Profile Tabs */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="flex space-x-8">
                        <button
                            onClick={() => setActiveTab('personal')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'personal'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {userType === 'individual' ? 'Personal Info' : 'Company Info'}
                        </button>

                        {userType === 'individual' && (
                            <button
                                onClick={() => setActiveTab('resumes')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'resumes'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                {`Resume (${profile.resumes?.length || 0}/1)`}
                            </button>
                        )}

                        {userType === 'individual' && (
                            <button
                                onClick={() => setActiveTab('skills')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'skills'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Skills & Experience
                            </button>
                        )}
                        {userType === 'company' && (
                            <button
                                onClick={() => setActiveTab('uploadReport')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'uploadReport'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Upload Report
                            </button>
                        )}
                    </nav>
                </div>

                {/* Tab Content */}
                {activeTab === 'uploadReport' && userType === 'company' && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
                        <h2 className="text-xl font-bold text-gray-900 mb-6">Resume Upload Report</h2>

                        {uploadReport ? (
                            <div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">


                                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                                        <div className="text-sm text-blue-600 font-medium">Total Attempts</div>
                                        <div className="text-2xl font-bold text-blue-900">{uploadReport.stats?.total_uploads || 0}</div>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <div className="text-sm text-green-600 font-medium">Successful</div>
                                        <div className="text-2xl font-bold text-green-900">{uploadReport.stats?.successful_uploads || 0}</div>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4 text-center">
                                        <div className="text-sm text-red-600 font-medium">Failed</div>
                                        <div className="text-2xl font-bold text-red-900">{uploadReport.stats?.failed_uploads || 0}</div>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                        <div className="text-sm text-yellow-600 font-medium">Duplicates</div>
                                        <div className="text-2xl font-bold text-yellow-900">{uploadReport.stats?.duplicate_uploads || 0}</div>
                                    </div>
                                </div>
                                
                                <h3 className="font-semibold text-gray-900 mb-4">Detailed Logs</h3>

                                {/* Filters */}
                                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                    <div className="flex flex-wrap items-end gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                                            <select
                                                value={uploadLogStatus}
                                                onChange={(e) => setUploadLogStatus(e.target.value)}
                                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                                            >
                                                <option value="all">All</option>
                                                <option value="success">Successful</option>
                                                <option value="processing">Processing</option>
                                                <option value="failed">Failed</option>
                                                <option value="duplicate">Duplicate</option>
                                                <option value="pending">Pending</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                                            <input
                                                type="date"
                                                value={uploadLogDateFrom}
                                                onChange={(e) => setUploadLogDateFrom(e.target.value)}
                                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                                            <input
                                                type="date"
                                                value={uploadLogDateTo}
                                                onChange={(e) => setUploadLogDateTo(e.target.value)}
                                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                setUploadLogStatus('all');
                                                setUploadLogDateFrom('');
                                                setUploadLogDateTo('');
                                            }}
                                            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>

                                {(() => {
                                    const uploads = uploadReport?.uploads || [];
                                    const fromTs = uploadLogDateFrom ? new Date(`${uploadLogDateFrom}T00:00:00`).getTime() : null;
                                    const toTs = uploadLogDateTo ? new Date(`${uploadLogDateTo}T23:59:59`).getTime() : null;

                                    const filtered = uploads.filter((u) => {
                                        const rowStatus = String(u.status || '').trim().toLowerCase();
                                        const selectedStatus = String(uploadLogStatus || 'all').toLowerCase();
                                        const normalizedRowStatus = rowStatus === 'successful' ? 'success' : rowStatus;
                                        const statusOk = selectedStatus === 'all' ? true : normalizedRowStatus === selectedStatus;

                                        const uTs = u.upload_date ? new Date(u.upload_date).getTime() : NaN;

                                        let dateOk = true;
                                        if (fromTs !== null && !Number.isNaN(uTs)) {
                                            dateOk = dateOk && uTs >= fromTs;
                                        }
                                        if (toTs !== null && !Number.isNaN(uTs)) {
                                            dateOk = dateOk && uTs <= toTs;
                                        }

                                        return statusOk && dateOk;
                                    });

                                    const selectable = filtered.filter((u) => Number(u.resume_id) > 0);
                                    const selectableIds = selectable.map((u) => Number(u.resume_id));
                                    const allVisibleSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedCompanyResumeIds.includes(id));

                                    const toggleVisible = () => {
                                        if (allVisibleSelected) {
                                            setSelectedCompanyResumeIds(prev => prev.filter(id => !selectableIds.includes(id)));
                                            return;
                                        }
                                        setSelectedCompanyResumeIds(prev => {
                                            const next = [...prev];
                                            for (const id of selectableIds) {
                                                if (next.includes(id)) continue;
                                                if (next.length >= 10) break;
                                                next.push(id);
                                            }
                                            if (selectableIds.length > 10 || next.length >= 10) {
                                                toast.warning('Maximum 10 resumes can be selected at one time');
                                            }
                                            return next;
                                        });
                                    };

                                    return filtered.length > 0 ? (
                                        <div className="space-y-3">
                                            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                                <div className="flex flex-wrap items-center gap-3">
                                                    <span className="text-sm font-semibold text-gray-700">Selected: {selectedCompanyResumeIds.length}/10</span>
                                                    <button
                                                        type="button"
                                                        disabled={companyResumeBusy || selectedCompanyResumeIds.length === 0}
                                                        onClick={() => runCompanyResumeAction('reextract', selectedCompanyResumeIds)}
                                                        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {companyResumeBusy ? 'Processing...' : 'Re-extract Selected'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={companyResumeBusy || selectedCompanyResumeIds.length === 0}
                                                        onClick={() => runCompanyResumeAction('delete', selectedCompanyResumeIds)}
                                                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        Delete Selected
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    disabled={companyResumeBusy}
                                                    onClick={fetchUploadReport}
                                                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                                >
                                                    Refresh
                                                </button>
                                            </div>

                                            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                                <table className="w-full text-sm text-left">
                                                    <thead className="bg-gray-50 border-b border-gray-200">
                                                        <tr>
                                                            <th className="px-4 py-3 font-medium text-gray-600">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={allVisibleSelected}
                                                                    onChange={toggleVisible}
                                                                    disabled={companyResumeBusy || selectableIds.length === 0}
                                                                    className="h-4 w-4"
                                                                    aria-label="Select visible resumes"
                                                                />
                                                            </th>
                                                            <th className="px-4 py-3 font-medium text-gray-600">Date</th>
                                                            <th className="px-4 py-3 font-medium text-gray-600">File Name</th>
                                                            <th className="px-4 py-3 font-medium text-gray-600">Size (KB)</th>
                                                            <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                                                            <th className="px-4 py-3 font-medium text-gray-600">Reason</th>
                                                            <th className="px-4 py-3 font-medium text-gray-600">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {filtered.map((upload, idx) => {
                                                            const resumeId = Number(upload.resume_id || 0);
                                                            const manageable = resumeId > 0;
                                                            const selected = manageable && selectedCompanyResumeIds.includes(resumeId);
                                                            return (
                                                                <tr key={upload.id || `${upload.original_filename}-${idx}`} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-3">
                                                                        {manageable ? (
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selected}
                                                                                onChange={() => toggleCompanyResumeSelection(resumeId)}
                                                                                disabled={companyResumeBusy}
                                                                                className="h-4 w-4"
                                                                                aria-label={`Select ${upload.original_filename || 'resume'}`}
                                                                            />
                                                                        ) : (
                                                                            <span className="text-gray-300">—</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatKolkataDateTime(upload.upload_date)}</td>
                                                                    <td className="px-4 py-3 font-medium text-gray-900 max-w-[220px] truncate" title={upload.original_filename}>{upload.original_filename || 'Unknown'}</td>
                                                                    <td className="px-4 py-3 text-gray-600">{upload.file_size ? Math.round(upload.file_size / 1024) : 0}</td>
                                                                    <td className="px-4 py-3">
                                                                        <span className={`inline-flex rounded-full px-2 py-1 text-[10px] font-medium uppercase tracking-wider ${
                                                                            upload.status === 'success' ? 'bg-green-100 text-green-800' :
                                                                            upload.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                                            upload.status === 'duplicate' ? 'bg-yellow-100 text-yellow-800' :
                                                                            upload.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                                                                            'bg-gray-100 text-gray-800'
                                                                        }`}>
                                                                            {upload.status}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-gray-600 min-w-[220px]">{upload.reason || '-'}</td>
                                                                    <td className="px-4 py-3 whitespace-nowrap">
                                                                        {manageable ? (
                                                                            <div className="flex items-center gap-2">
                                                                                <button
                                                                                    type="button"
                                                                                    disabled={companyResumeBusy}
                                                                                    onClick={() => runCompanyResumeAction('reextract', [resumeId])}
                                                                                    className="rounded bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
                                                                                >
                                                                                    Re-extract
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    disabled={companyResumeBusy}
                                                                                    onClick={() => runCompanyResumeAction('delete', [resumeId])}
                                                                                    className="rounded bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                                                                                >
                                                                                    Delete
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-xs text-gray-400">No resume record</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-center py-8 border border-gray-200 rounded-lg bg-gray-50">No uploads found in the log.</p>
                                    );
                                })()}
                            </div>
                        ) : (
                            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-b-2 border-blue-600 rounded-full"></div></div>
                        )}
                    </div>
                )}
                
                {activeTab === 'personal' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left Column - Profile Info */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Profile Card */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div
                                    className="h-48 bg-gradient-to-r from-blue-500 to-purple-600 relative"
                                    style={{
                                        backgroundImage: profile.user?.coverImage ? `url(${profile.user.coverImage})` : `url(${STATIC_COVER_IMAGE})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    {isEditing && (
                                        <button className="absolute top-4 right-4 px-4 py-2 bg-white/90 backdrop-blur-sm text-gray-700 font-medium rounded-lg hover:bg-white">
                                            Change Cover
                                        </button>
                                    )}
                                </div>

                                <div className="px-8 pb-8 relative">
                                    {/* Profile Photo */}
                                    <div className="absolute -top-16 left-8">
                                        <div className="relative">
                                            <img
                                                src={userType === 'individual'
                                                    ? (profile.user?.profileImage || STATIC_PROFILE_IMAGE)
                                                    : (profile.company?.logoUrl || STATIC_COMPANY_IMAGE)
                                                }
                                                alt="Profile"
                                                className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                                            />
                                            {isEditing && userType === 'company' && (
                                                <label className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 cursor-pointer">
                                                    <span>📷</span>
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="image/*"
                                                        onChange={handleLogoUpload}
                                                        disabled={uploadingLogo}
                                                    />
                                                </label>
                                            )}
                                            {isEditing && userType === 'individual' && (
                                                <button className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100">
                                                    <span>✏️</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="pt-20">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                {isEditing ? (
                                                    userType === 'individual' ? (
                                                        <div className="space-y-4">
                                                            <input
                                                                type="text"
                                                                value={profile.user?.firstName || ''}
                                                                onChange={(e) => handleChange('user', 'firstName', e.target.value)}
                                                                className="text-3xl font-bold text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                                                                placeholder="First Name"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={profile.user?.lastName || ''}
                                                                onChange={(e) => handleChange('user', 'lastName', e.target.value)}
                                                                className="text-xl text-gray-600 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                                                                placeholder="Last Name"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={profile.company?.companyName || ''}
                                                            onChange={(e) => handleChange('company', 'companyName', e.target.value)}
                                                            className="text-3xl font-bold text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                                                            placeholder="Company Name"
                                                        />
                                                    )
                                                ) : (
                                                    <>
                                                        <h2 className="text-3xl font-bold text-gray-900">
                                                            {userType === 'individual'
                                                                ? `${profile.user?.firstName || ''} ${profile.user?.lastName || ''}`
                                                                : (profile.company?.companyName || 'Company Name')
                                                            }
                                                        </h2>
                                                        <p className="text-lg text-gray-600 mt-2">
                                                            {userType === 'individual'
                                                                ? (profile.profile?.headline || 'No headline added')
                                                                : (profile.company?.industry || 'No industry specified')
                                                            }
                                                        </p>
                                                    </>
                                                )}

                                                <div className="flex items-center space-x-4 mt-4">
                                                    <span className="flex items-center text-gray-600">
                                                        <span className="mr-2">📍</span>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                value={userType === 'individual' ? (profile.user?.location || '') : (profile.company?.location || '')}
                                                                onChange={(e) => handleChange(userType === 'individual' ? 'user' : 'company', 'location', e.target.value)}
                                                                className="bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500"
                                                                placeholder="Location"
                                                            />
                                                        ) : (
                                                            userType === 'individual'
                                                                ? (profile.user?.location || 'Not specified')
                                                                : (profile.company?.location || 'Not specified')
                                                        )}
                                                    </span>
                                                    <span className="flex items-center text-gray-600">
                                                        <span className="mr-2">✉️</span>
                                                        {profile.user?.email || ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Bio/Description */}
                                        <div className="mt-8">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4">
                                                {userType === 'individual' ? 'About' : 'Company Description'}
                                            </h3>
                                            {isEditing ? (
                                                <textarea
                                                    value={userType === 'individual' ? (profile.profile?.bio || '') : (profile.company?.description || '')}
                                                    onChange={(e) => handleChange(
                                                        userType === 'individual' ? 'profile' : 'company',
                                                        userType === 'individual' ? 'bio' : 'description',
                                                        e.target.value
                                                    )}
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    rows="4"
                                                    placeholder={userType === 'individual' ? "Tell us about yourself..." : "Describe your company..."}
                                                />
                                            ) : (
                                                <p className="text-gray-600 whitespace-pre-line">
                                                    {userType === 'individual'
                                                        ? (profile.profile?.bio || 'No bio added yet.')
                                                        : (profile.company?.description || 'No description added yet.')}
                                                </p>
                                            )}
                                        </div>

                                        {/* Additional Info for Company */}
                                        {userType === 'company' && (
                                            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="flex items-center space-x-3">
                                                    <span>🌐</span>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={profile.company?.website || ''}
                                                            onChange={(e) => handleChange('company', 'website', e.target.value)}
                                                            className="bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                                                            placeholder="Website"
                                                        />
                                                    ) : (
                                                        <span className="text-gray-700">{profile.company?.website || 'No website'}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <span>📱</span>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={profile.company?.phone || ''}
                                                            onChange={(e) => handleChange('company', 'phone', e.target.value)}
                                                            className="bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                                                            placeholder="Phone"
                                                        />
                                                    ) : (
                                                        <span className="text-gray-700">{profile.company?.phone || 'No phone'}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <span>📊</span>
                                                    {isEditing ? (
                                                        <select
                                                            value={profile.company?.size || ''}
                                                            onChange={(e) => handleChange('company', 'size', e.target.value)}
                                                            className="bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                                                        >
                                                            <option value="">Select Size</option>
                                                            <option value="1-10">1-10 employees</option>
                                                            <option value="11-50">11-50 employees</option>
                                                            <option value="51-200">51-200 employees</option>
                                                            <option value="201-500">201-500 employees</option>
                                                            <option value="500+">500+ employees</option>
                                                        </select>
                                                    ) : (
                                                        <span className="text-gray-700">{profile.company?.size || 'Not specified'}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <span>🎯</span>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={profile.company?.establishedYear || ''}
                                                            onChange={(e) => handleChange('company', 'establishedYear', e.target.value)}
                                                            className="bg-transparent border-b border-gray-300 focus:outline-none focus:border-blue-500 w-full"
                                                            placeholder="Established Year"
                                                        />
                                                    ) : (
                                                        <span className="text-gray-700">{profile.company?.establishedYear || 'Not specified'}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Stats */}
                        <div className="space-y-8">
                            {userType === 'individual' ? (
                                <>
                                    <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl p-6 text-white">
                                        <h3 className="text-lg font-bold mb-4">Profile Strength</h3>
                                        <div className="mb-6">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-blue-100">
                                                    {profile.profile?.profileStrength >= 80 ? 'Excellent' :
                                                        profile.profile?.profileStrength >= 60 ? 'Good' :
                                                            profile.profile?.profileStrength >= 40 ? 'Fair' : 'Poor'}
                                                </span>
                                                <span className="font-bold">{profile.profile?.profileStrength || 0}%</span>
                                            </div>
                                            <div className="w-full bg-blue-400 rounded-full h-2">
                                                <div
                                                    className="bg-white h-2 rounded-full transition-all duration-500"
                                                    style={{ width: `${profile.profile?.profileStrength || 0}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="w-full px-4 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50"
                                        >
                                            Complete Profile
                                        </button>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Stats</h3>
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Resume</span>
                                                <span className="font-bold text-blue-600">
                                                    {profile.resumes?.length > 0 ? 'Uploaded' : 'Not Uploaded'}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Skills</span>
                                                <span className="font-bold text-blue-600">{profile.skills?.length || 0}</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Experience</span>
                                                <span className="font-bold text-blue-600">{profile.experiences?.length || 0} positions</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600">Education</span>
                                                <span className="font-bold text-blue-600">{profile.education?.length || 0} degrees</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">Company Overview</h3>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm text-gray-600">Industry</p>
                                            <p className="font-medium text-gray-900">{profile.company?.industry || 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Company Type</p>
                                            <p className="font-medium text-gray-900">{profile.company?.companyType || 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Size</p>
                                            <p className="font-medium text-gray-900">{profile.company?.size || 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600">Established</p>
                                            <p className="font-medium text-gray-900">{profile.company?.establishedYear || 'Not specified'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'resumes' && userType === 'individual' && (
                    <div className="space-y-8">
                        {/* Resume History */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        {userType === 'individual' ? 'My Resume' : 'Company Resumes'}
                                    </h3>
                                    <p className="text-gray-600 mt-1">
                                        {userType === 'individual'
                                            ? 'View the resume already attached to your account'
                                            : 'View company candidate resumes'
                                        }
                                    </p>
                                </div>

                            </div>

                            {userType === 'company' && (
                                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-medium text-gray-700">
                                            Selected: {selectedCompanyResumeIds.length}/10
                                        </span>
                                        <button
                                            type="button"
                                            disabled={companyResumeBusy || selectedCompanyResumeIds.length === 0}
                                            onClick={() => runCompanyResumeAction('reextract', selectedCompanyResumeIds)}
                                            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
                                        >
                                            {companyResumeBusy ? 'Processing...' : 'Re-extract Selected'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={companyResumeBusy || selectedCompanyResumeIds.length === 0}
                                            onClick={() => runCompanyResumeAction('delete', selectedCompanyResumeIds)}
                                            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-50"
                                        >
                                            Delete Selected
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={companyResumeBusy}
                                        onClick={fetchCompanyResumes}
                                        className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                    >
                                        Refresh
                                    </button>
                                </div>
                            )}

                            {/* Info messages */}
                            {false && userType === 'individual' && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-start">
                                        <span className="text-blue-600 text-xl mr-3">💡</span>
                                        <div>
                                            <h4 className="font-semibold text-blue-800 mb-1">Resume Guidelines</h4>
                                            <p className="text-sm text-blue-700">
                                                You can only have one active resume at a time. Uploading a new resume will replace your current one.
                                                Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG, WEBP (Max 5MB)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {false && userType === 'company' && (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-start">
                                        <span className="text-purple-600 text-xl mr-3">📦</span>
                                        <div>
                                            <h4 className="font-semibold text-purple-800 mb-1">Company Resume Management</h4>
                                            <p className="text-sm text-purple-700">
                                                You can upload multiple resumes for different candidates. Each resume will be stored separately.
                                                Supported formats: PDF, DOC, DOCX, PNG, JPG, JPEG, WEBP (Max 5MB each)
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Resumes Display */}
                            {profile.resumes && profile.resumes.length > 0 ? (
                                <div className="space-y-6">
                                    {profile.resumes.map((resume) => (
                                        <div key={resume.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                            {/* Resume Header */}
                                            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-200">
                                                <div className="flex items-center gap-3">
                                                    {userType === 'company' && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCompanyResumeIds.includes(Number(resume.id))}
                                                            onChange={() => toggleCompanyResumeSelection(resume.id)}
                                                            disabled={companyResumeBusy}
                                                            className="h-4 w-4"
                                                            aria-label={`Select ${resume.original_filename || 'resume'}`}
                                                        />
                                                    )}
                                                    <span className="text-2xl">{getFileIcon(resume.original_filename)}</span>
                                                    <div>
                                                        <h4 className="font-medium text-gray-900">{resume.original_filename}</h4>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <span className="text-xs text-gray-500">
                                                                Uploaded: {formatDate(resume.upload_date)}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                Size: {formatFileSize(resume.file_size)}
                                                            </span>
                                                            {resume.is_current && userType === 'individual' && (
                                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                                                    Current
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleViewResume(resume)}
                                                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadResume(resume)}
                                                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700"
                                                    >
                                                        Download
                                                    </button>
                                                    {userType === 'company' && (
                                                        <button
                                                            onClick={() => runCompanyResumeAction('reextract', [resume.id])}
                                                            disabled={companyResumeBusy}
                                                            className="px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded hover:bg-amber-600 disabled:opacity-50"
                                                        >
                                                            Re-extract
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => userType === 'company' ? runCompanyResumeAction('delete', [resume.id]) : handleRemoveResume(resume.id)}
                                                        className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Resume Details for individuals */}
                                            {userType === 'individual' && renderResumeDetailsTable(resume.id)}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <span className="text-6xl mb-4 inline-block">📄</span>
                                    <h4 className="text-xl font-semibold text-gray-900 mb-2">
                                        {userType === 'individual' ? 'No Resume Uploaded' : 'No Resumes Uploaded'}
                                    </h4>
                                    <p className="text-gray-600 mb-4">
                                        {userType === 'individual'
                                            ? 'No resume is attached to this profile yet.'
                                            : 'No candidate resumes found for this company.'
                                        }
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Extracted Data Summary - Only for individuals */}
                        {userType === 'individual' && profile.resumes?.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Resume Summary</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <span className="text-2xl mb-2 inline-block">📊</span>
                                        <h4 className="font-semibold text-gray-900 mb-1">Skills</h4>
                                        <p className="text-2xl font-bold text-blue-600">{profile.skills?.length || 0}</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <span className="text-2xl mb-2 inline-block">💼</span>
                                        <h4 className="font-semibold text-gray-900 mb-1">Experience</h4>
                                        <p className="text-2xl font-bold text-green-600">{profile.experiences?.length || 0}</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <span className="text-2xl mb-2 inline-block">🎓</span>
                                        <h4 className="font-semibold text-gray-900 mb-1">Education</h4>
                                        <p className="text-2xl font-bold text-purple-600">{profile.education?.length || 0}</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <span className="text-2xl mb-2 inline-block">📁</span>
                                        <h4 className="font-semibold text-gray-900 mb-1">ATS Score</h4>
                                        <p className="text-2xl font-bold text-orange-600">
                                            {profile.resumes[0]?.ats_score || 0}%
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Skills & Experience Tab - Only for individuals */}
                {activeTab === 'skills' && userType === 'individual' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Skills Section */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Skills Card */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900">Skills & Expertise</h3>
                                        <p className="text-gray-600 mt-1">Skills extracted from your resume</p>
                                    </div>
                                    {isEditing && (
                                        <button
                                            onClick={handleAddSkill}
                                            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-500 flex items-center"
                                        >
                                            <span className="mr-2">+</span>
                                            Add Skill
                                        </button>
                                    )}
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    {profile.skills && profile.skills.length > 0 ? (
                                        profile.skills.map((skill, index) => (
                                            <div
                                                key={index}
                                                className="group relative px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 font-medium rounded-lg"
                                            >
                                                {skill}
                                                {isEditing && (
                                                    <button
                                                        onClick={() => handleRemoveSkill(index)}
                                                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                                    >
                                                        <span className="text-xs">✕</span>
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 w-full">
                                            <span className="text-4xl mb-3 inline-block">🔧</span>
                                            <p className="text-gray-600">No skills added yet. Upload a resume to extract skills automatically.</p>
                                            {!isEditing && (
                                                <button
                                                    onClick={() => setActiveTab('resumes')}
                                                    className="mt-4 px-6 py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100"
                                                >
                                                    Go to Resume Upload
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Experience Section */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-6">Work Experience</h3>
                                {profile.experiences && profile.experiences.length > 0 ? (
                                    <div className="space-y-6">
                                        {profile.experiences.map((exp, index) => (
                                            <div key={index} className="relative pl-6 border-l-2 border-blue-200 pb-6 last:pb-0">
                                                <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-500 rounded-full"></div>
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <h4 className="font-bold text-gray-900">{exp.title || 'Position'}</h4>
                                                        <p className="text-gray-600">{exp.company || 'Company'}</p>
                                                        <p className="text-sm text-gray-500 mt-1">
                                                            {exp.start_date || 'Start Date'} - {exp.end_date || 'Present'}
                                                        </p>
                                                        {exp.description && (
                                                            <p className="text-gray-600 mt-2 text-sm">{exp.description}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <span className="text-4xl mb-3 inline-block">💼</span>
                                        <p className="text-gray-600">No work experience added yet.</p>
                                        <p className="text-sm text-gray-500 mt-2">Resume analysis will appear here after a resume is attached.</p>
                                    </div>
                                )}
                            </div>

                            {/* Education Section */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-6">Education</h3>
                                {profile.education && profile.education.length > 0 ? (
                                    <div className="space-y-6">
                                        {profile.education.map((edu, index) => (
                                            <div key={index} className="relative pl-6 border-l-2 border-green-200 pb-6 last:pb-0">
                                                <div className="absolute -left-2 top-0 w-4 h-4 bg-green-500 rounded-full"></div>
                                                <div>
                                                    <h4 className="font-bold text-gray-900">{edu.degree || 'Degree'}</h4>
                                                    <p className="text-gray-600">{edu.institution || 'Institution'}</p>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {edu.year || 'Year'} {edu.field ? `• ${edu.field}` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <span className="text-4xl mb-3 inline-block">🎓</span>
                                        <p className="text-gray-600">No education information added yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Column - Quick Actions */}
                        <div className="space-y-8">
                            <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-xl p-6 text-white">
                                <h3 className="text-lg font-bold mb-4">Smart Resume Analysis</h3>
                                <p className="text-purple-100 mb-6">
                                    Resume parsing automatically extracts skills, experience, and education when a resume is attached.
                                </p>
                                <button
                                    onClick={() => setActiveTab('resumes')}
                                    className="w-full px-4 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-purple-50"
                                >
                                    Manage Resume
                                </button>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Tips</h3>
                                <ul className="space-y-3 text-sm text-gray-600">
                                    <li className="flex items-start">
                                        <span className="text-green-500 mr-2">✓</span>
                                        <span>Keep your resume and profile details up to date</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-500 mr-2">✓</span>
                                        <span>Skills are automatically extracted</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-500 mr-2">✓</span>
                                        <span>Review your current resume before applying</span>
                                    </li>
                                    <li className="flex items-start">
                                        <span className="text-green-500 mr-2">✓</span>
                                        <span>Higher profile strength = better job matches</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;
